/**
 * Machine skill / facet filter — roster dial dropdown (not native select).
 * Soft≠Live: filters only the painted roster; never invents membership.
 */
import { useEffect, useRef, useState } from "react";
import { html, M, stop } from "./ui.js";
import { skillLabelOf } from "./machine-skill-labels.js";

/** Iris open — origin top, compositor-safe scale/opacity. */
const DIAL_IRIS = { type: "spring", stiffness: 460, damping: 30, mass: 0.62 };
const DIAL_OPT = { type: "tween", duration: 0.22, ease: [0.16, 1, 0.3, 1] };

export default function MachineSkillFilter({
  tabs = [],
  value = "all",
  total = 0,
  onChange,
  compact = false,
  ariaLabel = "Filter by skill",
  shortLabel = "Skills",
  allSay = "Every claimed unit",
}) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const cur = String(value || "all");
  const items = [
    { id: "all", label: "All", count: total, say: allSay },
    ...tabs.map((t) => ({
      id: t.id,
      label: t.label || skillLabelOf(t.id),
      count: t.count,
      say: t.say || (String(t.count) + " unit" + (t.count === 1 ? "" : "s")),
    })),
  ];
  const active = items.find((x) => x.id === cur) || items[0];
  const filtered = cur !== "all";
  const optionN = Math.max(0, items.length - 1);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const t = setTimeout(() => {
      addEventListener("pointerdown", onDoc, true);
      addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      removeEventListener("pointerdown", onDoc, true);
      removeEventListener("keydown", onKey);
    };
  }, [open]);

  return html`
    <div className="mh-filter" ref=${root}
      data-open=${open ? "1" : "0"}
      data-compact=${compact ? "1" : "0"}
      data-on=${filtered ? "1" : "0"}
      data-dial="1"
      data-lux="1">
      <button type="button" className="mh-filter__trig"
        aria-haspopup="listbox" aria-expanded=${open ? "true" : "false"}
        aria-label=${ariaLabel + " · " + active.label + " · " + active.count}
        title=${shortLabel + " · " + active.label + " · " + active.count}
        onClick=${(e) => { stop(e); setOpen((v) => !v); }}>
        <span className="mh-filter__copy">
          <small className="mh-filter__k">${shortLabel}</small>
          <b className="mh-filter__v">${active.label}</b>
        </span>
        <em className="mh-filter__n" title="Matching units">${active.count}</em>
        <i className="mh-filter__chev" data-open=${open ? "1" : "0"} aria-hidden="true"/>
      </button>
      ${open ? html`
        <${M.div} className="mh-filter__pop" role="listbox" aria-label=${ariaLabel}
          data-lux="1" data-motion="iris"
          style=${{ transformOrigin: "top center" }}
          initial=${{ opacity: 0, y: -12, scaleY: 0.82, filter: "blur(6px)" }}
          animate=${{ opacity: 1, y: 0, scaleY: 1, filter: "blur(0px)" }}
          exit=${{ opacity: 0, y: -8, scaleY: 0.9, filter: "blur(4px)" }}
          transition=${DIAL_IRIS}
          onClick=${stop}>
          <span className="mh-filter__pop-glow" aria-hidden="true"/>
          <span className="mh-filter__pop-scan" aria-hidden="true"/>
          <header className="mh-filter__hd">
            <span>${shortLabel}</span>
            <small>${optionN} options · ${total} units</small>
          </header>
          <div className="mh-filter__list">
            ${items.map((it, idx) => html`
              <${M.button} key=${it.id} type="button" role="option"
                className="mh-filter__opt"
                aria-selected=${cur === it.id ? "true" : "false"}
                data-on=${cur === it.id ? "1" : "0"}
                title=${it.label + " · " + (it.say || "")}
                initial=${{ opacity: 0, x: -10 }}
                animate=${{ opacity: 1, x: 0 }}
                transition=${{ ...DIAL_OPT, delay: Math.min(idx, 12) * 0.028 }}
                onClick=${() => { setOpen(false); onChange && onChange(it.id); }}>
                <i className="mh-filter__mark" aria-hidden="true"/>
                <span className="mh-filter__opt-copy">
                  <b>${it.label}</b>
                  <small>${it.say || (it.count + " unit" + (it.count === 1 ? "" : "s"))}</small>
                </span>
                <em className="mh-filter__opt-n">${it.count}</em>
              <//>`)}
          </div>
        <//>` : null}
    </div>`;
}
